import { apiError, type ApiError } from '../../services/apiError';

export interface WorkflowDefaults {
  alcohol_workflow_enabled: boolean;
  alcohol_send_to_lab_enabled: boolean;
  manual_override_enabled: boolean;
}
export interface CustomerAccount extends WorkflowDefaults {
  id: number;
  account_number: string;
  account_name: string | null;
  is_archived: boolean;
}
export const workflowLabels: Record<keyof WorkflowDefaults, string> = {
  alcohol_workflow_enabled: 'Alcohol workflow',
  alcohol_send_to_lab_enabled: 'Alcohol send to lab',
  manual_override_enabled: 'Manual override',
};
export function validDefaults(value: unknown): value is WorkflowDefaults {
  const defaults = value as WorkflowDefaults | null;
  return (
    !!defaults &&
    Object.keys(workflowLabels).every((key) => typeof defaults[key as keyof WorkflowDefaults] === 'boolean')
  );
}
export function validAccount(value: unknown): value is CustomerAccount {
  const account = value as CustomerAccount | null;
  return (
    validDefaults(account) &&
    typeof account.account_number === 'string' &&
    !!account.account_number.trim() &&
    (account.account_name === null || typeof account.account_name === 'string') &&
    typeof account.is_archived === 'boolean' &&
    Number.isSafeInteger(account.id) &&
    account.id > 0
  );
}
export function accountSaveError(failure: unknown): ApiError | null {
  const error = apiError(failure, 'Customer accounts');
  if (!error) return null;
  if (error.status === 400) {
    const { code, detail, ...fields } = error.fields;
    return { ...error, fields, message: 'Check the highlighted fields and try again.' };
  }
  if (error.status === 403)
    return { ...error, message: 'You do not have permission to change customer accounts.' };
  if (error.status === 404)
    return { ...error, message: 'This customer account is no longer available. Refresh the table.' };
  if (error.status === 413)
    return { ...error, message: 'The request is too large. Shorten the account number or name.' };
  if (error.status && error.status >= 500)
    return { ...error, message: 'The account could not be saved. Please try again.' };
  return error;
}
